import React, { useCallback, useEffect, useState } from 'react';
import { apiJson } from './api';

/** Emails judged not to carry an invoice from one of your vendors: release one to a vendor, or reject it. */
export default function SetAsideEmails({ tick, onChanged }) {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows((await apiJson('/api/intake/set-aside')) || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => {
    if (!rows.length || vendors.length) return;
    apiJson('/api/vendors').then((v) => setVendors(v || [])).catch(() => {});
  }, [rows.length, vendors.length]);

  if (!rows.length && !error) return null;

  const act = async (id, action, body) => {
    setError('');
    try {
      await apiJson(`/api/intake/set-aside/${id}/${action}`, { method: 'POST', body });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid="set-aside-emails">
      <div>
        <h2 className="font-semibold text-ink">Emails set aside ({rows.length})</h2>
        <p className="text-sm text-ink-muted">
          These did not look like an invoice from one of your suppliers, so they were not added to your invoices.
        </p>
      </div>
      {error && <p className="text-sm text-red-700" data-testid="set-aside-error">{error}</p>}
      <ul className="space-y-2">
        {rows.map((r) => <SetAsideRow key={r.id} row={r} vendors={vendors} onAct={act} />)}
      </ul>
    </section>
  );
}

function SetAsideRow({ row, vendors, onAct }) {
  const [vendorId, setVendorId] = useState('');
  const [remember, setRemember] = useState(true);
  const t = row.triage || {};
  const read = t.read || {};
  return (
    <li className="rounded bg-white border border-amber-100 p-3 text-sm" data-testid={`set-aside-${row.id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <span className="font-medium text-ink">{row.subject || '(no subject)'}</span>
        <span className="text-ink-muted">{row.sender}</span>
      </div>
      <p className="text-ink-muted">{row.filename}{read.supplier ? ` · read as ${read.supplier}` : ''}
        {read.number ? ` · ${read.number}` : ''}{read.total ? ` · ${read.total}` : ''}</p>
      <p className="text-amber-800" data-testid={`set-aside-reason-${row.id}`}>{t.reason}</p>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <select aria-label="Supplier" value={vendorId} onChange={(e) => setVendorId(e.target.value)}
          className="border rounded px-2 py-1">
          <option value="">It is an invoice from…</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <label className="flex items-center gap-1 text-ink-muted">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          remember this sender
        </label>
        <button type="button" disabled={!vendorId} data-testid={`set-aside-release-${row.id}`}
          className="px-3 py-1 rounded bg-primary-600 text-white disabled:opacity-50"
          onClick={() => onAct(row.id, 'release', { vendor_id: vendorId, remember_sender: remember })}>
          Add to invoices
        </button>
        <button type="button" data-testid={`set-aside-reject-${row.id}`}
          className="px-3 py-1 rounded border border-gray-300 text-ink"
          onClick={() => onAct(row.id, 'reject')}>
          Not an invoice
        </button>
      </div>
    </li>
  );
}
